// Arbeitsplatz (Schnitt 1): Texte und kleine Helfer. Der Server liefert nur Status
// und Codes; jeder deutsche Satz entsteht hier — ohne Zahlen, ohne Betraege.
import type {
  ArbeitsplatzBestellSpur,
  ArbeitsplatzContainerSpur,
  ArbeitsplatzSchritt,
  ArbeitsplatzSchrittKey,
  ArbeitsplatzSchrittStatus,
  BestellSchrittKey,
  OrderArbeitsplatz,
  PostfachSchrittKey,
  PurchaseShipment,
} from "@/api/purchase";

export interface SchrittText {
  kurz: string;
  aufgabe: string;
  wer: string;
}

export const SCHRITT_TEXTE: Record<ArbeitsplatzSchrittKey, SchrittText> = {
  packliste: { kurz: "Packliste", aufgabe: "Packliste hochladen und bestätigen", wer: "Einkauf" },
  packdaten: { kurz: "Packdaten", aufgabe: "Packdaten und Mengen prüfen", wer: "Einkauf" },
  transport: { kurz: "Transport", aufgabe: "Transport und Termine", wer: "Einkauf" },
  ankunft: { kurz: "Ankunft", aufgabe: "Ankunft prüfen und Wareneingang eröffnen", wer: "Lagerleitung" },
  einlagern: { kurz: "Einlagern", aufgabe: "Einlagern und Wareneingang abschließen", wer: "Lager / Lagerleitung" },
  bestand: { kurz: "Bestand buchen", aufgabe: "Bestand buchen", wer: "Lagerleitung (PIN) · nur Plattform" },
  einstandspreis: { kurz: "Misch-EK", aufgabe: "Einstandspreis festlegen (Misch-EK)", wer: "Preise (PIN)" },
};

export const BESTELL_TEXTE: Record<BestellSchrittKey, SchrittText> = {
  lieferungen: { kurz: "Lieferungen", aufgabe: "Restmengen klären", wer: "Einkauf" },
  // Nutzerentscheidung 19.10.2026: schreiben duerfen hier nur Preisrechte (Rollen-Postfach: Admin/Preise).
  invoice: { kurz: "Invoice", aufgabe: "Rechnungssumme erfassen", wer: "Admin/Preise" },
  zahlungen: { kurz: "Zahlungen", aufgabe: "Zahlungen erfassen", wer: "Admin/Preise" },
  kosten: { kurz: "Kosten", aufgabe: "Kosten erfassen", wer: "Admin/Preise" },
};

/** Nur im Rollen-Postfach: Bestellung laeuft ohne Container (die Akte zeigt dafuer den Knopf „Container anlegen“). */
export const CONTAINER_ANLEGEN_TEXT: SchrittText = { kurz: "Container anlegen", aufgabe: "Ersten Container anlegen", wer: "Einkauf" };

export const STATUS_TEXTE: Record<ArbeitsplatzSchrittStatus, string> = {
  done: "erledigt",
  now: "jetzt dran",
  fix: "Klärung nötig",
  wait: "wartet",
  gate: "abgeschaltet",
  idle: "noch nicht dran",
};

export const GATE_TEXT = "Bestand-Push ist auf diesem System abgeschaltet.";

export const CODE_TEXTE: Record<string, string> = {
  packliste_fehlt: "Die Packliste ist noch nicht bestätigt.",
  no_items: "Der Container hat noch keine bestätigten Positionen.",
  unlinked_item: "Eine Containerposition ist keiner Bestellposition zugeordnet.",
  packing_missing: "Kartons und Stück je Karton müssen bestätigt werden.",
  packing_inconsistent: "Kartonzahl und Stück je Karton passen nicht zur Gesamtmenge.",
  packstation_invalid: "Packstation-Kartons brauchen gültige Kartons und Stück je Karton.",
  geparkt: "Der Container ist geparkt.",
  in_produktion: "Der Container ist noch in Produktion. Wird er verschifft, den Status auf „Unterwegs“ setzen.",
  abholbereit: "Der Container ist abholbereit, aber noch nicht verschifft. Wird er verschifft, den Status auf „Unterwegs“ setzen.",
  eta: "Der Container ist noch nicht unterwegs. Die Ankunft ist erst nach der Verschiffung dran (ETA).",
  reparatur: "Status ist „Im Wareneingang“, aber der Wareneingang fehlt. Die Reparatur läuft über denselben sicheren Weg.",
  packdaten_vor_reparatur: "Vor der Reparatur müssen Packliste und Packdaten vollständig bestätigt sein.",
  we_offen: "Der Wareneingang ist noch nicht abgeschlossen. Das Lager erfasst.",
  we_abschliessen: "Alle Positionen sind vollständig. Die Lagerleitung schließt den Wareneingang ab.",
  we_unvollstaendig: "Der Wareneingang hat offene oder teilweise Positionen. Fehlmengen je Position klären (Ist übernehmen oder Storno), dann abschließen.",
  we_klaerfaelle_offen: "Der Wareneingang hat offene Klärfälle. Die Lagerleitung löst sie mit Begründung, dann wird abgeschlossen.",
  kosten_erwartet: "Ein Kostenposten steht noch auf „erwartet“ und blockiert den Misch-EK.",
  kosten_fehlen: "Für diesen Container ist kein Kostenposten angelegt (Spedition, Zoll). Ohne Posten kein Misch-EK.",
  kurs_fehlt: "Keine Zahlung mit USD- und EUR-Betrag. Der effektive Kurs fehlt.",
  bestand_nicht_gebucht: "Bestand noch nicht gebucht: für diesen Container fehlt der Bestands-Nachweis. Erst „Bestand buchen“, dann Misch-EK — der Server lehnt den Misch-EK vorher ab.",
  gate_zu: GATE_TEXT,
  invoice_fehlt: "Die Rechnungssumme (Invoice) ist nicht erfasst.",
  teilbezahlt: "Die Bestellung ist teilweise bezahlt.",
  offen: "Noch keine Zahlung erfasst.",
  rest_offen: "Mindestens eine Position ist noch nicht vollständig auf Container verteilt. Den Rest liefern lassen oder bewusst ausweisen.",
  unzugeordnet: "Containerpositionen ohne Bestellposition zählen bei keiner Position mit (Altcontainer oder gelöschte Position).",
  container_fehlt: "Die Bestellung läuft, aber es gibt noch keinen Container.",
};

// Postfach-Karten im Klartext (21.10.2026): Laufzettel und konkreter Kosten-Grund.

/** Laufzettel der Bestellung: Rechnung und Zahlung zuerst — danach wird am haeufigsten gesucht. */
export const LAUFZETTEL_BESTELLUNG: Array<{ key: BestellSchrittKey; kurz: string }> = [
  { key: "invoice", kurz: "Rechnung" },
  { key: "zahlungen", kurz: "Zahlung" },
  { key: "kosten", kurz: "Kosten" },
  { key: "lieferungen", kurz: "Lieferungen" },
];

/** Zeichen und Zustand eines Laufzettel-Felds. Offen heisst ✗; „noch nicht dran“ ist ○, kein Fehler. */
export function laufzettelZeichen(status: ArbeitsplatzSchrittStatus): string {
  if (status === "done") return "✓";
  if (status === "idle") return "○";
  if (status === "gate") return "–";
  return "✗";
}

/** In der Bestellung fehlt etwas (Rechnung, Zahlung); im Container wartet ein Schritt auf etwas anderes. */
export function laufzettelZustand(status: ArbeitsplatzSchrittStatus, bestellung: boolean): string {
  return status === "wait" && bestellung ? "fehlt" : STATUS_TEXTE[status];
}

const KOSTEN_TYP_TEXT: Record<"spedition" | "zoll" | "sonstig", string> = {
  spedition: "Spedition",
  zoll: "Zoll",
  sonstig: "Sonstige Kosten",
};

/** „Kostenposten Zoll (C1) steht noch auf „erwartet“ …“ — Container-Nr. nur, wenn die Karte mehrere meint. */
export function kostenErwartetText(
  posten: Array<{ container_nr: number; typ: "spedition" | "zoll" | "sonstig"; label: string | null }>,
  mitContainer: boolean,
): string {
  const namen = posten.map((k) => {
    const art = KOSTEN_TYP_TEXT[k.typ] ?? k.typ;
    const label = k.label && k.label.trim() !== "" && k.label !== art ? ` „${k.label}“` : "";
    return `${art}${label}${mitContainer ? ` (C${k.container_nr})` : ""}`;
  });
  const verb = posten.length === 1 ? "steht" : "stehen";
  return `Kostenposten ${namen.join(", ")} ${verb} noch auf „erwartet“. Betrag erfassen oder auf „entfällt“ setzen.`;
}

export function ohneKostenpostenText(containerNr: number[]): string {
  const wer = containerNr.map((n) => `C${n}`).join(", ");
  return `${wer} ${containerNr.length === 1 ? "hat" : "haben"} noch keinen Kostenposten (Spedition, Zoll). Ohne Posten kein Misch-EK.`;
}

/** Klaerfall Schritt 7: Status eingelagert, aber kein Bestands-Nachweis (z. B. von Hand gesetzt). */
export const KLAERFALL_BESTAND_TEXT =
  "Klärfall: Status „eingelagert“ ohne Bestands-Nachweis. Bestand prüfen und klären — Zuständig: Admin/Preise.";

export function codeText(code: string): string {
  return CODE_TEXTE[code] ?? `Klärung: ${code}`;
}

/** Farbleiste je Spur (wie die Monday-Gruppen): Projekt-Tokens, zyklisch. */
const SPUR_FARBEN = ["var(--chart-5)", "var(--chart-3)", "var(--chart-4)", "var(--chart-2)"];
export function spurFarbe(index: number): string {
  return SPUR_FARBEN[index % SPUR_FARBEN.length]!;
}
export const BESTELL_FARBE = "var(--ink-2)";

export type Spur = ArbeitsplatzContainerSpur | ArbeitsplatzBestellSpur;
export type IrgendeinSchritt = ArbeitsplatzSchritt<ArbeitsplatzSchrittKey> | ArbeitsplatzSchritt<BestellSchrittKey>;

/** Der Schritt, den die Aufgabe-Karte zeigt: "Jetzt dran", sonst der erste offene; null = alles erledigt. */
export function aktuellerSchritt(spur: Spur, jetztDran: OrderArbeitsplatz["jetzt_dran"]): IrgendeinSchritt | null {
  const schritte = spur.schritte as IrgendeinSchritt[];
  if (jetztDran && jetztDran.spur_key === spur.key) {
    const hit = schritte.find((s) => s.nr === jetztDran.schritt);
    if (hit) return hit;
  }
  for (const status of ["now", "fix", "wait", "gate"] as const) {
    const hit = schritte.find((s) => s.status === status);
    if (hit) return hit;
  }
  return null;
}

export function schrittText(schritt: { key: PostfachSchrittKey }): SchrittText {
  if (schritt.key === "container_anlegen") return CONTAINER_ANLEGEN_TEXT;
  if (schritt.key in SCHRITT_TEXTE) return SCHRITT_TEXTE[schritt.key as ArbeitsplatzSchrittKey];
  if (schritt.key in BESTELL_TEXTE) return BESTELL_TEXTE[schritt.key as BestellSchrittKey];
  // Unbekannter Key (neueres Backend als diese Seite): Key zeigen statt abstuerzen.
  return { kurz: schritt.key, aufgabe: schritt.key, wer: "" };
}

/** Die eine Zahl einer Bezeichnung wie „Container 2“ (1–999), sonst null. */
function labelNummer(label: string | null): number | null {
  const m = label?.match(/^\D*(\d{1,3})\D*$/);
  const n = m ? Number(m[1]) : 0;
  return n > 0 ? n : null;
}

/**
 * C-Nummer je Container (id → Nummer). Traegt jede Bezeichnung genau eine Zahl und keine doppelt
 * („Container 1“, „Container 2“), gilt diese Zahl — C1 ist der Container, auf dem „Container 1“ steht
 * (22.10.2026). Sonst die Position in `reihenfolge` (Spuren des Servers, dann die uebrigen der Akte).
 */
export function containerNummern(shipments: ReadonlyArray<Pick<PurchaseShipment, "id" | "label">>, reihenfolge: readonly number[]): Map<number, number> {
  const ids = [...reihenfolge, ...shipments.map((s) => s.id).filter((id) => !reihenfolge.includes(id))];
  const ausLabel = shipments.map((s) => labelNummer(s.label));
  const eindeutig = ausLabel.every((n) => n !== null) && new Set(ausLabel).size === ausLabel.length;
  return new Map(ids.map((id, i) => {
    const s = shipments.findIndex((x) => x.id === id);
    return [id, eindeutig && s >= 0 ? ausLabel[s]! : i + 1];
  }));
}

/** "C2 · EGSU9431010": Nummer aus `containerNummern`, dann Container-Nr. oder Label. */
export function containerTitel(shipment: PurchaseShipment | undefined, laufendeNr: number): string {
  const nr = shipment?.container_no ?? shipment?.label ?? (shipment ? `#${shipment.id}` : "?");
  return `C${laufendeNr} · ${nr}`;
}

export function statusFarbeKlasse(status: ArbeitsplatzSchrittStatus): string {
  switch (status) {
    case "done": return "bg-success";
    case "now": return "bg-primary";
    case "fix": return "bg-coral";
    case "wait": return "bg-warn";
    case "gate": return "bg-muted-2/60";
    default: return "bg-panel-2";
  }
}

export function statusChipKlasse(status: ArbeitsplatzSchrittStatus): string {
  switch (status) {
    case "done": return "bg-success-soft text-success border-success/40";
    case "now": return "bg-primary text-primary-foreground border-primary";
    case "fix": return "bg-coral-soft text-coral border-coral/40";
    case "wait": return "bg-warn-soft text-warn border-warn/40";
    case "gate": return "bg-panel-2 text-ink-2 border-line";
    default: return "bg-panel-2 text-ink-2 border-line";
  }
}
